// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { EducBox, ResultCard, InputRow, EDUC, fmt } from './shared';

const ISEECalc = () => {
  const [componenti, setComponenti] = useState('2');
  const [figliMinorenni, setFigliMinorenni] = useState('0');
  const [conDisabilita, setConDisabilita] = useState('0');
  const [redditi, setRedditi] = useState('35000');
  const [redditiEsenti, setRedditiEsenti] = useState('0');
  const [patrimonioImmob, setPatrimonioImmob] = useState('0');
  const [patrimonioMob, setPatrimonioMob] = useState('5000');
  const [agevolOpen, setAgevolOpen] = useState(null);

  const n = Math.max(1, parseInt(componenti) || 1);
  const franchigaMob = 6000 + (n - 1) * 2000;
  const scalaEquiv = n === 1 ? 1.0 : n === 2 ? 1.57 : n === 3 ? 2.04 : n === 4 ? 2.46 : n === 5 ? 2.85 : 2.85 + (n - 5) * 0.35;
  const R = parseFloat(redditi) || 0;
  const RE = parseFloat(redditiEsenti) || 0;
  const PI = parseFloat(patrimonioImmob) || 0;
  const PM = parseFloat(patrimonioMob) || 0;

  const ISE = (R + RE) + Math.max(0, PI - 52500) * 0.2 + Math.max(0, PM - franchigaMob) * 0.2;
  const ISEE = ISE / scalaEquiv;

  const getFascia = (v) => {
    if (v < 3000) return { label: 'Fascia molto bassa', color: '#1D7A52', bg: 'rgba(29,122,82,0.15)' };
    if (v < 9530) return { label: 'Fascia bassa', color: '#3D7A52', bg: 'rgba(61,122,82,0.15)' };
    if (v < 15000) return { label: 'Fascia medio-bassa', color: '#4A9E80', bg: 'rgba(74,158,128,0.15)' };
    if (v < 25000) return { label: 'Fascia media', color: '#C9A84C', bg: 'rgba(201,168,76,0.15)' };
    if (v < 40000) return { label: 'Fascia medio-alta', color: '#E8903A', bg: 'rgba(232,144,58,0.15)' };
    return { label: 'Fascia alta', color: '#888780', bg: 'rgba(136,135,128,0.15)' };
  };
  const fascia = getFascia(ISEE);

  const agevolazioni = [
    { soglia: 3000, label: 'ISEE < €3.000', items: ['Reddito di inclusione', 'Assegno di inclusione massimo', 'Bonus bollette massimo luce e gas', 'Gratuità mense scolastiche'] },
    { soglia: 9530, label: 'ISEE < €9.530', items: ['Bonus asilo nido massimo €3.600/anno', 'Agevolazioni INPS per famiglie', 'Esenzione ticket sanitario molte ASL', 'Riduzione massima rette universitarie'] },
    { soglia: 15000, label: 'ISEE < €15.000', items: ['Bonus bollette luce e gas', 'Agevolazioni universitarie DSU', 'Bonus trasporto pubblico'] },
    { soglia: 25000, label: 'ISEE < €25.000', items: ['Riduzioni rette scolastiche comunali', 'Bonus nido ridotto €1.500/anno'] },
    { soglia: 40000, label: 'ISEE < €40.000', items: ['Detrazioni figli a carico standard', 'Accesso edilizia residenziale pubblica', 'Borse di studio universitarie regionali'] },
  ].filter(a => ISEE < a.soglia);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <EducBox text={EDUC.isee} />
      <div className="grid sm:grid-cols-2 gap-6 mb-6">
        <div>
          <p className="text-sm font-medium text-[#E8E8E4] mb-3">Il tuo nucleo</p>
          <div className="space-y-3">
            <InputRow label="Numero componenti" value={componenti} onChange={setComponenti} suffix="persone" min="1" />
            <InputRow label="di cui figli minorenni" value={figliMinorenni} onChange={setFigliMinorenni} suffix="figli" />
            <InputRow label="di cui con disabilità certificata" value={conDisabilita} onChange={setConDisabilita} />
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-[#E8E8E4] mb-3">Redditi</p>
          <div className="space-y-3">
            <InputRow label="Reddito complessivo lordo (€)" value={redditi} onChange={setRedditi} prefix="€" tooltip="Somma redditi dichiarati nel 730 o CU di tutti i componenti" />
            <InputRow label="Redditi esenti o non dichiarati (€)" value={redditiEsenti} onChange={setRedditiEsenti} prefix="€" tooltip="Pensioni invalidità, assegni familiari, rendite INAIL" />
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-[#E8E8E4] mb-3">Patrimonio</p>
          <div className="space-y-3">
            <InputRow label="Patrimonio immobiliare (€)" value={patrimonioImmob} onChange={setPatrimonioImmob} prefix="€" tooltip="Valore catastale rivalutato degli immobili. Prima casa: franchigia automatica di €52.500" />
            <InputRow label="Patrimonio mobiliare totale (€)" value={patrimonioMob} onChange={setPatrimonioMob} prefix="€" tooltip={`Conti correnti, depositi, titoli, fondi. Franchigia: €${fmt(franchigaMob)} con ${n} componenti`} />
          </div>
        </div>
      </div>
      <div className="p-6 rounded-2xl mb-6 text-center" style={{ background: fascia.bg, border: `1px solid ${fascia.color}40` }} data-testid="isee-result">
        <p className="text-sm text-[#888780] mb-1">ISEE stimato</p>
        <p className="text-4xl font-bold mb-2" style={{ color: fascia.color }}>€{fmt(ISEE)}</p>
        <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold" style={{ background: fascia.bg, color: fascia.color, border: `1px solid ${fascia.color}60` }}>
          {fascia.label}
        </span>
      </div>
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-[#E8E8E4] mb-3">Agevolazioni potenziali con il tuo ISEE</h3>
        {agevolazioni.length === 0 ? (
          <p className="text-sm text-[#888780] p-4 bg-[#252523] rounded-xl">Con ISEE superiore a €40.000 non si accede alle principali agevolazioni ISEE-dipendenti.</p>
        ) : (
          <div className="space-y-2">
            {agevolazioni.map((a, i) => (
              <div key={i} className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                <button onClick={() => setAgevolOpen(agevolOpen === i ? null : i)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/[0.04] transition-colors" data-testid={`isee-agev-${i}`}>
                  <span className="text-sm font-medium text-[#3D7A52]">{a.label}</span>
                  <ChevronRight className={`w-4 h-4 text-[#888780] transition-transform ${agevolOpen === i ? 'rotate-90' : ''}`} />
                </button>
                {agevolOpen === i && (
                  <div className="px-4 pb-3 bg-[#252523]">
                    <ul className="space-y-1">
                      {a.items.map((item, j) => (
                        <li key={j} className="text-sm text-[#888780] flex items-start gap-2"><span className="text-[#3D7A52] mt-0.5">•</span>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-[#888780] mt-4 p-3 bg-[#252523] rounded-xl" data-testid="isee-disclaimer">
        Simulazione indicativa basata su formula ISEE semplificata. Il valore reale può differire. Per la dichiarazione ufficiale rivolgiti a un CAF gratuitamente o usa il servizio INPS Precompilato online.
      </p>
</motion.div>
  );
};

export default ISEECalc;
